/**
 * English Language Pack
 */
export default {
  // Common
  common: {
    language: 'Language',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    networkError: 'Network error, please try again later',
    help: 'Help',
    logout: 'Logout',
    welcome: 'Welcome, {{username}}',
    get copyright() {
      // Lazy evaluation to ensure window.PidocBrand is loaded
      return (
        'Copyright © 2026 ' +
        ((window.PidocBrand && window.PidocBrand.copyright) || 'pidoc.cn') +
        '.'
      );
    },
    feedback:
      'Remote management is free to use. For large-scale deployment, please join our group. Issues or suggestions → QQ Group: 498908352',
    close: 'Close',
    refresh: 'Refresh',
    totalRecords: 'Total {{count}} records',
    devices: '{{count}} devices',
    operation: 'Action',
    unauthorized: 'Unauthorized',
    loginExpired: 'Login expired, please login again',
    adminAuthFailed: 'Authentication failed, please check admin credentials',
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Copy failed',
    copy: 'Copy',
    reset: 'Reset',
    create: 'Create',
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
    show: 'Show',
    hide: 'Hide',
    copied: 'Copied',
  },

  // Login Page
  login: {
    title: 'PiDOC',
    subtitle: 'Remote Device Management',
    username: 'Username',
    usernamePlaceholder: 'Username',
    usernameRequired: 'Please enter username',
    password: 'Password',
    passwordPlaceholder: 'Password',
    passwordRequired: 'Please enter password',
    loginButton: 'Login',
    loginSuccess: 'Login successful!',
    loginFailed: 'Login failed',
    tooManyAttempts: 'Too many failed attempts, please try again later',
    noAccount: 'No account? Register now',
    forgotPassword: 'Forgot password?',
    emailNotVerified: 'Please verify your email before logging in',
  },

  // Register Page
  register: {
    title: 'Register Account',
    username: 'Username',
    usernamePlaceholder: 'Username (3-20 letters or digits)',
    usernameRequired: 'Please enter username',
    usernameMin: 'Username must be at least 3 characters',
    usernameMax: 'Username cannot exceed 20 characters',
    usernamePattern: 'Username can only contain letters and digits',
    email: 'Email',
    emailPlaceholder: 'Email',
    emailRequired: 'Please enter email',
    emailInvalid: 'Please enter a valid email address',
    password: 'Password',
    passwordPlaceholder: 'Password (at least 6 characters)',
    passwordRequired: 'Please enter password',
    passwordMin: 'Password must be at least 6 characters',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm Password',
    confirmPasswordRequired: 'Please confirm password',
    confirmPasswordMismatch: 'Passwords do not match',
    registerButton: 'Register',
    registerSuccess: 'Registration successful!',
    registerFailed: 'Registration failed',
    haveAccount: 'Already have an account? Login now',
    emailSent: 'Verification email sent, please check your inbox and click the verification link',
    redirectingIn: 'Redirecting to login page in {{seconds}} seconds...',
  },

  // Forgot Password Page
  forgotPassword: {
    title: 'Reset Password',
    emailSent: 'Email Sent',
    emailPlaceholder: 'Registered Email',
    emailRequired: 'Please enter email address',
    emailInvalid: 'Please enter a valid email address',
    sendButton: 'Send Reset Email',
    backButton: 'Back to Login',
    successMessage: 'Please check your email for password reset link',
    instruction:
      'We have sent a password reset link to your email.<br/>Please click the link within 1 hour to reset your password.',
  },

  // Reset Password Page
  resetPassword: {
    title: 'Reset Password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'New Password (at least 6 characters)',
    newPasswordRequired: 'Please enter new password',
    newPasswordMin: 'Password must be at least 6 characters',
    confirmPassword: 'Confirm New Password',
    confirmPasswordPlaceholder: 'Confirm New Password',
    confirmPasswordRequired: 'Please confirm new password',
    confirmPasswordMismatch: 'Passwords do not match',
    resetButton: 'Reset Password',
  },

  // Dashboard
  dashboard: {
    overview: 'Overview',
    metrics: 'Metrics',
    devices: 'Devices',
    keys: 'Keys',
    bandwidth: 'Bandwidth',
    ssh: 'Remote Access',
    messages: 'Messages',
    forward: 'App Forward',
    installTokens: 'Install Tokens',
  },

  // Web Forward
  forward: {
    subdomain: 'Subdomain',
    subdomainRequired: 'Subdomain is required',
    subdomainPattern: 'Lowercase letter start, lowercase letters/digits/hyphens only, max 20 chars',
    targetDevice: 'Target Device',
    targetPort: 'Target Port',
    target: 'Target',
    pathPrefix: 'Path Prefix',
    selectDevice: 'Select Device',
    addRoute: 'Add Forward Route',
    editRoute: 'Edit Forward Route',
    forwardHint: 'HTTP forwarding requires an active Ops login session (valid for 30 days).',
    webForward: 'Web Forward',
    mcForward: 'MC Server Forward',
    mcServerAddress: 'Server Address',
    mcTarget: 'Target Device:Port',
    mcHint: 'Enter the server address (e.g. myserver.mc.pidoc.cn:25565) in your Minecraft client to connect to your device. Subdomain is globally unique and cannot be changed after creation.',
    mcDeleteTitle: 'Delete MC Forward',
    mcDeleteConfirm: 'Delete forward config for "{subdomain}.mc.pidoc.cn"?',
    mcAddModal: 'New MC Forward',
    mcEditModal: 'Edit MC Forward',
    mcSubdomainLabel: 'Subdomain',
    mcSubdomainRequired: 'Subdomain is required',
    mcSubdomainPattern: 'Lowercase letter start, lowercase letters/digits/hyphens only, max 20 chars',
    mcTargetDevice: 'Target Device',
    mcTargetDeviceRequired: 'Please select a device',
    mcTargetPort: 'Target Port',
    mcTargetPortRequired: 'Port is required',
    mcSelectDevice: 'Select device',
    // V2 Web Forward
    loadFailed: 'Failed to load forward list',
    confirmDeleteTitle: 'Delete Forward',
    confirmDelete: 'Delete forward config for "{subdomain}"?',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    fillAllFields: 'Please fill all fields',
    updateSuccess: 'Updated successfully',
    createSuccess: 'Created successfully',
    operationFailed: 'Operation failed',
    device: 'Device',
    port: 'Port',
    title: 'Web Forward',
    add: 'Add',
    edit: 'Edit',
    subdomainPlaceholder: 'Enter subdomain',
    pathPrefixPlaceholder: 'Enter path prefix, e.g. /api',
  },

  // Device Management
  deviceManagement: {
    title: 'Device Management',
    deviceAlias: 'Device Alias',
    hostname: 'Hostname',
    system: 'System',
    terminalVersion: 'Terminal Version',
    connectionStatus: 'Status',
    online: 'Online',
    offline: 'Offline',
    actions: 'Actions',
    detail: 'Detail',
    history: 'History',
    edit: 'Edit',
    evict: 'Evict',
    evictConfirm:
      'Are you sure you want to evict device "{{name}}"? The device and its authentication key will be permanently deleted.',
    evictSuccess: 'Eviction successful',
    evictFailed: 'Eviction failed',
    evictTitle: 'Evict Device',
    add: 'Add',
    loadFailed: 'Failed to load device list',
    unhealthy: 'Unhealthy',
    healthReasons: {
      expired_key: 'Expired key, please re-generate a new key',
      wrong_device: 'Key belongs to another device, please re-generate a new key',
      duplicate_connection: 'Device already connected',
      timeout: 'Connection timeout',
      app_not_running: 'App not running',
      network_error: 'Network error',
    },
    detailTitle: 'Device Details',
    basicInfo: 'Basic Information',
    systemInfo: 'System Information',
    runMode: 'Run Mode',
    runModeUser: 'User Mode',
    runModeSystem: 'System Mode',
    serialNumber: 'Serial Number',
    createTime: 'Created At',
    lastOnline: 'Last Online',
    updateTime: 'Updated At',
    os: 'Operating System',
    osVersion: 'OS Version',
    editTitle: 'Edit Device',
    aliasPlaceholder: 'Set an easily recognizable name for the device',
    aliasRule: 'Device alias can only contain lowercase letters (a-z), digits (0-9), and hyphens (-). It must start with a letter and be at most 32 characters.',
    updateSuccess: 'Update successful',
    updateFailed: 'Update failed',
    connectionHistory: 'Connection History',
    serverAddr: 'Server Addr',
    connectTime: 'Connected At',
    disconnectTime: 'Disconnected At',
    duration: 'Duration',
    clientIP: 'Client IP',
    disconnectReason: 'Disconnect Reason',
    keyDetail: 'Key Details',
    existingKey: 'Existing Unbound Key',
    existingKeyWarning: 'An unbound valid key already exists',
    keyName: 'Key Name',
    expireTime: 'Expires At',
    existingKeyTip:
      'Each account can only have one unbound valid key. Please delete the existing key before creating a new one.',
    installCommand: 'Install Command',
    installCommandWarning: '⚠️ Shown only once',
    installCommandTip:
      '⚠️ Key is valid for 6 hours and can only bind to one device. Please copy and run the install command on the device immediately.',
    installStep1: 'Step 1: Install PiDOC Client',
    installStep2: 'Step 2: Register to Device',
    // New: choose install token to generate install command
    selectInstallTokenTitle: 'Add Device - Choose Install Token',
    selectInstallToken: 'Select Install Token',
    installTokenEmpty: 'No install tokens available. Please create one in Key Management → Install Tokens first.',
    username: 'Install Account',
    installCommandForToken: 'Install command (token and account will be filled in automatically)',
    copyInstallCommand: 'Copy Install Command',
    installCommandCopied: 'Install command copied',
    globalEditionTip: 'You are on the Global edition (inst.sh), which does not support --customtoken yet. Please use a seed key or wait for the script to be updated.',
    bindDevice: 'Bind Device',
    openInstallTokenManagement: 'Create Install Token',
    step1Install: 'Step 1: Install PiDOC Client',
    step2InstallService: 'Step 2: Register to Device',
    installStep2Warning: '⚠️ This key is shown only once. Run the command on your device immediately.',
    copiedToClipboard: 'Install command copied to clipboard',
    keyCreated: 'Key created successfully',
    keyCreateFailed: 'Failed to create key',
    keyCheckFailed: 'Failed to check key',
    keyDeleted: 'Key deleted',
    keyDeleteFailed: 'Deletion failed',
    tags: 'Tags',
    tagPlaceholder: 'Type a tag name and press Enter',
    tagAddTag: 'Add Tag',
    tagSearch: 'Search tags...',
    tagRule: 'Tags can only contain lowercase letters, digits, hyphens and underscores, max 30 characters',
    tagExists: 'Tag already exists',
    fullInstallGuide: 'Full Installation Guide',
    viewFullGuide: 'View Full Installation Guide',
    show: 'Show',
    hide: 'Hide',
    uploadDownload: 'Speed',
    allStatus: 'All Status',
    allMode: 'All Modes',
    lastSeen: 'Last Seen',
    updated: 'Updated',
  },

  // Install Token Management
  installTokenManagement: {
    title: 'Install Tokens',
    tip: 'Install tokens are used for first-time device setup: pass the token to the install script to automatically redeem a login key and register the device.',
    create: 'New Token',
    createTitle: 'New Install Token',
    editTitle: 'Edit Install Token',
    token: 'Token',
    tokenPlaceholder: 'Enter token (visible chars, no spaces, 6-20 chars)',
    tokenHint: 'Token is hand-authored by you; devices redeem a login key with it at install time. Not editable after creation.',
    tokenInvalid: 'Invalid token format (visible characters only, no spaces, length 6-20)',
    note: 'Note',
    notePlaceholder: 'Enter a note',
    tags: 'Tags',
    tagsPlaceholder: 'Type a tag and press Enter, separate multiple tags with commas',
    enabled: 'Enabled',
    usedCount: 'Used',
    createdAt: 'Created At',
    operation: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Delete this install token? Devices that already used it are unaffected.',
    confirmDeleteTitle: 'Delete Install Token',
    createSuccess: 'Created successfully',
    updateSuccess: 'Updated successfully',
    deleteSuccess: 'Deleted successfully',
    createFailed: 'Failed to create',
    updateFailed: 'Failed to update',
    deleteFailed: 'Failed to delete',
    loadFailed: 'Failed to load token list',
    networkError: 'Network error, please try again later',
    empty: 'No install tokens yet',
    installCommand: 'Install command',
    copyInstall: 'Copy install command',
  },

  // Overview Page
  overview: {
    onlineDevices: 'Online Devices',
    boostBalance: 'Boost Traffic Balance',
    totalUpload24h: 'Total Upload (24h)',
    totalDownload24h: 'Total Download (24h)',
    aiTodayRequests: 'AI Requests (Today)',
    aiTodayTokens: 'AI Token Usage (Today)',
    aiUsageEmpty: 'No usage yet',
    request: 'Request',
    token: 'Token',
    accountInfo: 'Account Information',
    username: 'Username',
    email: 'Email',
    registerTime: 'Registered At',
    accountSecurity: 'Account Security',
    keyManagement: 'Key Management',
    changePassword: 'Change Password',
    versionHistory: 'Version History',
    currentVersion: 'Current',
    showMore: 'Show More',
    showLess: 'Show Less',
    passwordMismatch: 'New passwords do not match',
    passwordChanged: 'Password changed successfully, please login again',
    passwordChangeFailed: 'Failed to change password',
    oldPassword: 'Old Password',
    oldPasswordPlaceholder: 'Enter old password',
    oldPasswordRequired: 'Please enter old password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter new password (at least 6 characters)',
    newPasswordRequired: 'Please enter new password',
    newPasswordMin: 'Password must be at least 6 characters',
    confirmNewPassword: 'Confirm New Password',
    confirmNewPasswordPlaceholder: 'Enter new password again',
    confirmNewPasswordRequired: 'Please confirm new password',
    confirmPasswordMismatch: 'Passwords do not match',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Enter new password again',
    changePasswordWarning: 'You will need to login again after changing password. Please make sure you remember the new password.',
    changePasswordTip: '⚠️ Notice:',
    changePasswordTip1: '• You need to login again after changing password',
    changePasswordTip2: '• Please remember your new password',
    confirmChange: 'Confirm Change',
  },

  // Key Management
  keyManagement: {
    fortressKey: 'Fortress Key',
    agentKey: 'Agent Key',
  },

  // Agent Key Management (merged V1 + V2)
  agentKeyManagement: {
    loadFailed: 'Failed to load key list',
    networkError: 'Network error',
    createSuccess: 'Agent key created successfully!',
    createFailed: 'Creation failed',
    confirmRegenerateTitle: 'Regenerate Seed',
    confirmRegenerate: 'Confirm Regenerate',
    confirmRegenerateWarn:
      'After regeneration, the old key will become invalid and connections using that key will be disconnected. Continue?',
    regenerateSuccess: 'Key regenerated successfully!',
    regenerateFailed: 'Regeneration failed',
    confirmDeleteTitle: 'Delete Key',
    confirmDelete: 'Are you sure to delete key "{{name}}"? This action cannot be undone.',
    deleteSuccess: 'Key deleted successfully',
    deleteFailed: 'Failed to delete key',
    confirmRevoke: 'Confirm Revoke',
    revokeWarning: 'After revocation, connections using this key will be disconnected. Revoke?',
    revokeSuccess: 'Revocation successful',
    revokeFailed: 'Revocation failed',
    key: 'Key',
    keyName: 'Key Name',
    seed: 'Seed',
    createdAt: 'Created At',
    expiresAt: 'Expires At',
    copy: 'Copy',
    keyCopied: 'Key copied to clipboard',
    regenerate: 'Regenerate',
    revoke: 'Revoke',
    createAgentKey: 'Create Agent Key',
    maxKeyLimit: 'Each account can have at most one Agent key',
    confirmCreate: 'Confirm creating Agent key?',
    createTip1: '• Each account can only have one Agent key',
    createTip2: '• Agent key is used for Pidoc Agent tool',
    createTip3: '• Please keep your Agent key secure',
    title: 'Agent Key Management',
    create: 'Create Key',
    createWarning: '⚠️ Key is only shown once',
    keyDetail: 'Key Detail',
    installTip: '⚠️ Please run the install command on the device',
    show: 'Show',
    hide: 'Hide',
  },

  // Proxy Key Management (merged V1 + V2)
  proxyKeyManagement: {
    loadFailed: 'Failed to load key list',
    networkError: 'Network error',
    createSuccess: 'Key created successfully!',
    publicKeyAdded: 'Public key added successfully!',
    createFailed: 'Creation failed',
    confirmRevoke: 'Confirm Revoke',
    revokeWarning:
      'After revocation, devices using this private key will not be able to connect. Revoke?',
    revokeSuccess: 'Revocation successful',
    revokeFailed: 'Revocation failed',
    updateSuccess: 'Update successful',
    updateFailed: 'Update failed',
    keyName: 'Key Name',
    keyType: 'Key Type',
    fortressKey: 'Fortress Key',
    agentKey: 'Agent Key',
    fortressKeyTip: 'Fortress key is used for device connection authentication. Please copy it to the device after creation.',
    agentKeyTip: 'Agent key is used for Agent mode connection authentication.',
    publicKey: 'Public Key',
    seed: 'Seed',
    status: 'Status',
    createdAt: 'Created At',
    title: 'Proxy Key Management',
    create: 'Create Key',
    createProxyKey: 'Create Fortress Key',
    maxKeyLimit: 'Each account can have at most five keys (including Agent keys)',
    createNewKey: 'Create New Key',
    keyNamePlaceholder: 'e.g.: my-proxy-key',
    keyNameRequired: 'Please enter key name',
    createMode: 'Creation Mode',
    autoGenerate: 'Auto-generate key pair',
    useExisting: 'Use existing public key',
    publicKeyContent: 'Public Key Content',
    publicKeyPlaceholder: 'ssh-ed25519 AAAA...',
    publicKeyRequired: 'Please enter public key content',
    expiresAt: 'Expires At',
    confirmDeleteTitle: 'Delete Proxy Key',
    confirmDelete: 'Are you sure you want to delete this proxy key? This cannot be undone.',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Deletion failed',
    publicKeyTip: 'Paste OpenSSH format public key (e.g., ssh-ed25519 AAAA...)',
    keyDetail: 'Key Details',
    privateKeyDownload: 'Private Key Download',
    downloadNow: '⚠️ Download now',
    importantNotice: '⚠️ Important Notice',
    privateKeyWarning:
      'Private key is not stored on the server. Please download and keep it secure immediately! You cannot download it again after closing this window.',
    downloadPrivateKey: 'Download Private Key File',
    downloadSuccess: 'Private key downloaded successfully! Please keep it secure',
    downloadFailed: 'Download failed: {{error}}',
    usageGuide: 'Usage Guide',
    savePrivateKey: '1. Save private key to local file',
    changePermission: '2. Change file permission',
    configureSSH: '3. Configure SSH Config (Optional)',
    editKeyName: 'Edit Key Name',
    show: 'Show',
    hide: 'Hide',
  },

  // Email Verification
  verifyEmail: {
    pageTitle: 'Email Verification - pidoc',
    title: 'Email Verification',
    success: 'Verification Successful',
    failed: 'Verification Failed',
    redirecting: 'Redirecting to login page in {{seconds}} seconds...',
    gotoLogin: 'Go to Login',
    backHome: 'Back to Home',
    footer: 'pidoc - Remote Device Management Platform',
    home: 'Home',
    contactSupport: 'Contact Support',
    // Verification result - Title
    successTitle: 'Email Verified!',
    missingToken: 'Missing Token',
    invalidLink: 'Verification Failed',
    expiredLink: 'Link Expired',
    systemError: 'Verification Failed',
    // Verification result - Message
    successMessage: 'Your email has been verified. Redirecting to login page...',
    missingTokenMessage:
      'The link is invalid or has expired. Please register again or contact support.',
    invalidLinkMessage:
      'The verification link is invalid. It may have been used or does not exist.',
    expiredLinkMessage:
      'The verification link has expired. Please register again or contact support to resend.',
    systemErrorMessage: 'System error: {{error}}',
  },

  // SSH Page
  ssh: {
    title: 'Terminal & File Manager',
    pleaseLogin: 'Please login first',
    backToLogin: 'Back to Login',
    noConnection: 'No connections',
    newConnection: '+ New Connection',
    newConnectionTitle: 'New Connection',
    connectionMode: 'Connection Mode',
    sshMode: 'SSH Terminal',
    sftpMode: 'SFTP Files',
    vncMode: 'VNC Desktop',
    selectDevice: 'Select Device',
    selectDevicePlaceholder: 'Please select a device',
    selectDeviceRequired: 'Please select a device',
    username: 'Username',
    usernamePlaceholder: 'e.g.: root',
    usernameRequired: 'Please enter username',
    port: 'Port',
    portPlaceholder: 'Default: 22',
    portRequired: 'Please enter port',
    authMethod: 'Authentication Method',
    authMethodRequired: 'Please select authentication method',
    passwordAuth: 'Password',
    privateKeyAuth: 'Private Key',
    password: 'Password',
    passwordPlaceholder: 'Enter password',
    passwordRequired: 'Please enter password',
    privateKey: 'Private Key File',
    privateKeyPlaceholder: 'Select private key file',
    privateKeyRequired: 'Please upload private key file',
    privateKeyLoaded: '✓ Private key loaded ({{preview}}...)',
    serviceHintSsh: 'This requires SSH service to be running on your device. PiDOC does not enable it automatically.',
    serviceHintVnc: 'This requires VNC service to be running on your device. PiDOC does not enable it automatically.',
    serviceHintDocLink: 'How to enable:',
    serviceHintDocUrl: 'https://pidoc.cn/docs/computers/remote-access',
    connect: 'Connect',
    connectionFailed: 'SSH connection failed',
    deviceNotFound: 'Device not found',
    loadDevicesFailed: 'Failed to load device list',
    loginExpired: 'Login expired, please login again',
    sftpConnectionFailed: 'SFTP connection failed',
    refreshWarning:
      'Refreshing the page will disconnect all SSH/SFTP connections. Are you sure you want to continue?',
    useAgent: 'Use ssh-agent passwordless login',
    useAgentHint: 'When checked, connect via local ssh-agent without a password (user-mode devices only)',
    agentUnavailable: 'ssh-agent passwordless login unavailable, switched to password/private key. Provide credentials and reconnect.',
  },

  // Metrics
  metrics: {
    minutes: 'minutes',
    hours: 'hours',
  },

  // Bandwidth Monitoring
  bandwidthMonitoring: {
    title: 'Bandwidth Monitoring',
    autoRefresh: 'Auto Refresh',
    refresh: 'Refresh',
    bandwidthConfig: 'Bandwidth Configuration (Dynamic Allocation)',
    baseBandwidth: 'Base Bandwidth per Device (Dedicated)',
    globalBoostPool: 'Global Boost Pool (Shared)',
    boostTrafficBalance: 'Boost Traffic Balance',
    onlineDevices: 'Online Devices',
    boostEnabled: 'Boost enabled, high-demand devices get more bandwidth.',
    boostDisabled: 'Balance below 15MB, only base bandwidth available.',
    trafficSummary: 'Traffic Summary (Last 24 Hours)',
    totalUpload: 'Total Upload',
    totalDownload: 'Total Download',
    totalTraffic: 'Total Traffic',
    deviceRealtimeTraffic: 'Device Real-time Traffic',
    realtimeTrafficTip:
      'Note: When device is online but has no traffic, both rate and cumulative traffic are 0',
    noOnlineDevices: 'No online devices',
    deviceAlias: 'Device Alias',
    online: 'Online',
    uploadRate: 'Upload Rate',
    downloadRate: 'Download Rate',
    totalRate: 'Total Rate',
    cumulativeTraffic: 'Cumulative Traffic',
    connectionTime: 'Connection Time',
    viewStatistics: 'View Statistics',
    deviceTrafficStats: 'Device Traffic Statistics',
    close: 'Close',
    time: 'Time',
    upload: 'Upload',
    download: 'Download',
    averageRate: 'Average Rate',
    rewardedBoostTraffic: 'Rewarded Boost Traffic',
    consumedBoostTraffic: 'Consumed Boost Traffic',
    fetchBandwidthConfigFailed: 'Failed to fetch bandwidth configuration',
    fetchTrafficSummaryFailed: 'Failed to fetch traffic summary',
    fetchDeviceTrafficStatsFailed: 'Failed to fetch device traffic statistics',
  },

  // Device Card
  deviceCard: {
    sshConnect: 'SSH Connect',
    vncRemoteDesktop: 'VNC Remote Desktop',
    rdpRemoteDesktop: 'RDP Remote Desktop',
    edit: 'Edit',
    delete: 'Delete',
    serialNumber: 'Serial Number',
    hostname: 'Hostname',
    deviceOffline: 'Device is currently offline',
  },

  // Messages
  messages: {
    title: 'Messages',
    feedbackTab: 'Feedback',
    systemTab: 'System',
    sendMessage: 'Send Message',
    sendTitle: 'Send Message',
    titleLabel: 'Title',
    titlePlaceholder: 'Enter message title',
    titleRequired: 'Please enter title',
    titleMax: 'Title cannot exceed 200 characters',
    contentLabel: 'Content',
    contentPlaceholder: 'Enter message content',
    contentRequired: 'Please enter content',
    sendSuccess: 'Message sent successfully',
    sendFailed: 'Failed to send',
    replySuccess: 'Reply sent successfully',
    replyFailed: 'Failed to reply',
    replyPlaceholder: 'Enter reply content...',
    reply: 'Reply',
    replyCount: 'replies',
    replyEmpty: 'Please enter reply content',
    addAttachment: 'Add Attachment',
    attachmentLimit: 'Cannot upload more than {{max}} attachments',
    attachmentSizeLimit: 'File size cannot exceed 5MB',
    attachmentUploadSuccess: 'Attachment uploaded successfully',
    attachmentUploadFailed: 'Upload failed',
    statusFilter: 'Status Filter',
    statusUnread: 'Unread',
    statusRead: 'Read',
    statusClosed: 'Closed',
    markAsRead: 'Mark as Read',
    batchReadSuccess: 'Marked as read',
    close: 'Close',
    batchClose: 'Close',
    batchCloseTitle: 'Batch Close',
    batchCloseConfirm:
      'Are you sure you want to close {{count}} selected message(s)? Closed messages cannot be replied to.',
    batchCloseSuccess: 'Messages closed',
    operationFailed: 'Operation failed',
    sender: 'Sender',
    status: 'Status',
    time: 'Time',
    detail: 'Message Detail',
    loading: 'Loading...',
    listFailed: 'Failed to load message list',
    detailFailed: 'Failed to load message detail',
    unreadCountFailed: 'Failed to get unread count',
    total: 'Total {{count}}',
    admin: 'Admin',
    networkError: 'Network error',
    fileEmpty: 'File content is empty',
    noReplyAllowed: 'System messages do not support replies',
    messageClosed: 'Message is closed',
    justNow: 'Just now',
    ago: ' ago',
  },

  // Admin Panel
  admin: {
    networkError: 'Network error',
    justNow: 'Just now',
    minutesAgo: '{{count}} min ago',
    hoursAgo: '{{count}}h ago',
    close: 'Close',
    detail: 'Detail',
    refresh: 'Refresh',
    search: 'Search',
    reset: 'Reset',
    cancel: 'Cancel',
    send: 'Send',
    reply: 'Reply',
    copy: 'Copy',
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Copy failed',
    totalRecords: 'Total {{count}} records',
    totalOnlineDevices: 'Total {{count}} online devices',

    messageManagement: 'Message Management',
    sendToUser: 'Send Message to User',
    broadcastToAll: 'Broadcast to All Users',
    singleSend: 'Send',
    singleSendMessage: 'Send Message',
    broadcast: 'Broadcast',
    broadcastMessage: 'Broadcast',
    feedbackMessages: 'Feedback',
    systemMessages: 'System',
    title: 'Title',
    sender: 'Sender',
    status: 'Status',
    time: 'Time',
    action: 'Action',
    statusClosed: 'Closed',
    statusUnread: 'Unread',
    statusRead: 'Read',
    recipientUserId: 'Recipient User ID',
    enterUserId: 'Enter User ID',
    pleaseEnterUserId: 'Please enter User ID',
    pleaseEnterTitle: 'Please enter title',
    titleMaxLength: 'Title cannot exceed 200 characters',
    enterMessageTitle: 'Enter message title',
    content: 'Content',
    pleaseEnterContent: 'Please enter content',
    enterMessageContent: 'Enter message content',
    addAttachment: 'Add Attachment',
    maxAttachments: 'Max {{count}} attachments',
    fileSizeLimit: 'File size cannot exceed 5MB',
    attachmentUploaded: 'Attachment uploaded',
    uploadFailed: 'Upload failed',
    messageSent: 'Message sent successfully',
    sendFailed: 'Failed to send',
    broadcastSent: 'Broadcast sent successfully',
    broadcastFailed: 'Broadcast failed',
    pleaseEnterReply: 'Please enter reply content',
    enterReplyContent: 'Enter reply content...',
    replySent: 'Reply sent successfully',
    replyFailed: 'Reply failed',
    messageClosed: 'Message closed',
    closeFailed: 'Failed to close',
    loadMessagesFailed: 'Failed to load messages',
    loadDetailFailed: 'Failed to load message detail',
    messageDetail: 'Message Detail',
    replyCount: 'Replies',

    userManagement: 'User Management',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    bannedLabel: 'Banned',
    verifiedEmail: 'Verified Email',
    totalDevicesLabel: 'Total Devices',
    onlineDevicesLabel: 'Online Devices',
    userList: 'User List',
    username: 'Username',
    email: 'Email',
    deviceCount: 'Devices',
    online: 'Online',
    keyCount: 'Keys',
    registerTime: 'Registered',
    ban: 'Ban',
    unban: 'Unban',
    confirmBanUser: 'Confirm Ban User',
    confirmBanContent:
      'Are you sure you want to ban user "{{name}}"? The user will be unable to login.',
    userBanned: 'User banned',
    banFailed: 'Ban failed',
    confirmUnbanUser: 'Confirm Unban User',
    confirmUnbanContent: 'Are you sure you want to unban user "{{name}}"?',
    userUnbanned: 'User unbanned',
    unbanFailed: 'Unban failed',
    loadUsersFailed: 'Failed to load user list',
    loadDevicesFailed: 'Failed to load device list',
    loadKeysFailed: 'Failed to load key list',
    userDetail: 'User Detail',
    statusNormal: 'Normal',
    verified: 'Verified',
    unverified: 'Unverified',
    lastLogin: 'Last Login',
    neverLoggedIn: 'Never logged in',
    deviceList: 'Devices',
    keyList: 'Keys',
    searchPlaceholder: 'Search username/email/ID',
    deviceAlias: 'Device Alias',
    serialNumber: 'Serial Number',
    hostname: 'Hostname',
    system: 'System',
    terminalVersion: 'Terminal Version',
    connectionStatus: 'Status',
    onlineStatus: 'Online',
    offlineStatus: 'Offline',
    lastOnline: 'Last Online',
    createTime: 'Created',
    keyName: 'Key Name',
    purpose: 'Purpose',
    purposeClient: 'Client',
    purposeProxy: 'Proxy',
    publicKey: 'Public Key',

    onlineDevices: 'Online Devices',
    devicesCount: '{{count}} devices',
    searchDevicePlaceholder: 'Search device/user/hostname',
    autoRefreshOn: 'On',
    autoRefreshOff: 'Off',
    loadOnlineDevicesFailed: 'Failed to load online devices',
    deviceId: 'Device ID',
    user: 'User',
    ip: 'IP',
    proxy: 'Proxy',
    connectionType: 'Connection Type',
    statusOnline: 'Online',
    connectionTime: 'Connected',
    onlineTime: 'Online Since',
    proxyStats: 'By Proxy: ',
    deviceDetail: 'Device Detail',
    connectionMethod: 'Connection',
    systemType: 'OS Type',
    systemVersion: 'OS Version',
    proxyId: 'Proxy ID',
    lastHeartbeat: 'Last Heartbeat',
    deviceCreateTime: 'Device Created',
    daysAgo: '{{count}} days ago',

    forwardRecords: 'Forward Records',
    userId: 'User ID',
    type: 'Type',
    startTime: 'Start Time',
    endTime: 'End Time',
    ongoing: 'Ongoing',
    duration: 'Duration',
    uploadMB: 'Upload(MB)',
    downloadMB: 'Download(MB)',
    totalTrafficMB: 'Total(MB)',
    sourceAddr: 'Source Addr',
    sourcePort: 'Source Port',
    destAddr: 'Dest Addr',
    destPort: 'Dest Port',
    searchUserPlaceholder: 'Search User ID/Username',
    forwardTypeFilter: 'Forward Type',
    all: 'All',
    dateFrom: 'From',
    dateTo: 'To',
    filterMinTraffic: 'Filter <1MB',
    loadForwardRecordsFailed: 'Failed to load forward records',
    adminLoggedIn: 'Admin logged in',

    menuUsers: 'Users',
    menuOnlineDevices: 'Online Devices',
    menuForwardRecords: 'Forward Records',
    menuMessages: 'Messages',
    menuServices: 'Service Discovery',
    menuDeviceConnections: 'Device Connections',
    proxyServices: 'Pidoc Proxy Services',
    webServices: 'Pidoc Web Services',
    region: 'Region',
    domain: 'Domain',
    address: 'Address',
    capacity: 'Capacity',
    load: 'Load',
    version: 'Version',
    versionNotReported: 'Not reported',
    startedAt: 'Started At',
    noServices: 'No services registered',
    loadServicesFailed: 'Failed to load service list',
  },

  // SFTP File Manager
  sftp: {
    connectionFailed: 'SFTP connection failed',
    closeSessionFailed: 'Failed to close session',
    loginExpired: 'Login expired',
    readDirFailed: 'Failed to read directory',
    downloadFailed: 'Download failed',
    downloadStarted: 'Download started: {{name}}',
    downloadComplete: 'Download complete: {{name}} ({{size}})',
    confirmDelete: 'Confirm Delete',
    confirmDeleteContent:
      'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
    deleteBtn: 'Delete',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    confirmBatchDelete: 'Confirm Batch Delete',
    confirmBatchDeleteContent:
      'Are you sure you want to delete {{count}} selected items? This action cannot be undone.',
    batchDeleteSuccess: 'Deleted {{ok}} items',
    batchDeletePartial: 'Deleted {{ok}} items, {{fail}} failed',
    filenameNoSlash: 'Filename cannot contain /',
    renameSuccess: 'Renamed successfully',
    renameFailed: 'Rename failed',
    mkdirSuccess: 'Created successfully',
    mkdirFailed: 'Creation failed',
    preparing: 'Preparing upload: {{name}} ({{size}})',
    uploadFailed: 'Upload failed',
    wsCreateFailed: 'WebSocket creation failed',
    networkErrorMsg: 'Network error',
    cancelled: 'Cancelled',
    batchUploadSuccess: 'Uploaded {{ok}} files',
    batchUploadPartial: 'Uploaded {{ok}}, {{fail}} failed',
    singleUploadSuccess: 'Upload successful: {{name}}',
    parentDir: 'Parent',
    refreshBtn: 'Refresh',
    newFolder: 'New Folder',
    uploadFile: 'Upload File',
    loadingText: 'Loading...',
    emptyDropHint: 'Directory is empty, drag files here to upload',
    emptyDropRelease: 'Drop files to upload',
    fileName: 'Name',
    fileSize: 'Size',
    modifiedTime: 'Modified',
    permissions: 'Permissions',
    rootDir: 'Root',
    upload: 'Upload',
    download: 'Download',
    downloadFiles: 'Download {{count}} files',
    deleteItems: 'Delete {{count}} items',
    downloadSingle: 'Download',
    renameSingle: 'Rename',
    deleteSingle: 'Delete',
    newFolderMenu: 'New Folder',
    itemCount: '{{count}} items',
    selectedItem: 'Selected: {{name}}',
    selectedItems: '{{count}} items selected',
    remainingTime: '{{time}} remaining',
    renameTitle: 'Rename',
    newFolderTitle: 'New Folder',
    create: 'Create',
    folderNamePlaceholder: 'Folder name',
    seconds: '{{n}}s',
    minutesSeconds: '{{m}}m {{s}}s',
    hoursMinutes: '{{h}}h {{m}}m',
    queueCount: ' | queued: {{count}} file(s)',
    dropNothing: 'No uploadable file detected',
    dropUnsupported: 'Folders and empty files cannot be uploaded',
    dropPartialSkipped: 'Skipped {{n}} folder(s)/empty file(s)',
    overwriteTitle: 'File already exists',
    overwriteContent:
      'These files already exist in the target folder: {{names}}. Uploading will overwrite them and cannot be undone.',
    overwriteConfirm: 'Overwrite',
    confirmDeleteDir: 'Delete directory',
    confirmDeleteDirContent:
      'The directory {{name}} and all of its contents will be permanently deleted. This cannot be undone.',
  },

  // SSH Terminal
  sshTerminal: {
    sessionNotFound: 'Session not found',
    connectingToDevice: 'Connecting to device...',
    wsConnected: '● WebSocket connected',
    authenticating: 'Authenticating...',
    errorPrefix: '✗ Error: ',
    disconnected: '● Disconnected (code: {{code}})',
    wsError: '✗ WebSocket error',
    connectionFailed: 'Connection failed: ',
  },

  // VNC Remote Desktop
  vnc: {
    noVNCNotLoaded: 'noVNC library not loaded yet, please refresh the page',
    containerNotReady: 'Container not ready, please retry',
    notLoggedIn: 'Not logged in, please login first',
    connected: 'VNC connected',
    disconnectedClean: 'VNC disconnected',
    disconnectedError: 'VNC connection lost: {{reason}}',
    unknownReason: 'unknown reason',
    passwordRequired: 'VNC server requires password',
    enterPassword: 'Enter VNC password: ',
    passwordNeeded: 'VNC password required to connect',
    securityFailed: 'VNC password incorrect or security verification failed',
    clipboardSynced: 'Clipboard synced to local',
    connectionFailed: 'Connection failed: ',
    authRetry: 'Authentication failed, please re-enter the password...',
    ctrlAltDelSent: 'Sent Ctrl+Alt+Del',
    viewOnlyMode: 'Switched to view-only mode',
    interactiveMode: 'Switched to interactive mode',
    clipboardEnabled: 'Clipboard sync enabled',
    clipboardDisabled: 'Clipboard sync disabled',
    qualityLevel: 'Quality level: {{level}}',
    compressionLevel: 'Compression level: {{level}}',
    confirmReboot: 'Are you sure you want to reboot the remote machine?',
    remoteDesktopTitle: 'Remote Desktop Connection',
    pleaseFromDeviceList: 'Please access from the device list',
    connectToRemoteDesktop: 'Connect to Remote Desktop',
    device: 'Device',
    username: 'Username',
    usernamePlaceholder: 'System login username',
    vncPort: 'VNC Port',
    portPlaceholder: 'Default 5900',
    vncPassword: 'VNC Password (optional)',
    vncPasswordPlaceholder: 'If VNC server requires password',
    connectBtn: 'Connect',
    connecting: 'Connecting...',
    fullscreen: 'Fullscreen',
    viewOnly: 'View Only',
    interactive: 'Interactive',
    settings: 'Settings',
    disconnect: 'Disconnect',
    connectionSettings: 'Connection Settings',
    clipboardSync: 'Clipboard Sync',
    readOnlyMode: 'Read-only Mode',
    qualityLabel: 'Quality Level',
    lowerIsHigher: 'Lower = Higher quality',
    compressionLabel: 'Compression Level',
    higherIsStronger: 'Higher = Stronger compression',
    desktop: 'Desktop',
  },
};
